<script lang="ts" setup>
import HeadlineListStepsSection from '../../HeadlineListStepsSection.vue';
import ScrollTable from '../../ScrollTable.vue';
import WhereToConsultCard from '../../WhereToConsult/WhereToConsultCard.vue';

defineProps<{
  imageNumber: number;
}>();
</script>

<template>
  <WhereToConsultCard title="iDeCoの始め方" :image-number="imageNumber">
    <p>
      iDeCoで老後を見据えた資産形成に取り組むには、以下の手順に沿って手続きを行う必要があります。
    </p>
    <HeadlineListStepsSection>
      <li><a href="#card2Step1">STEP1：加入資格の確認</a></li>
      <li><a href="#card2Step2">STEP2：掛金の決定</a></li>
      <li><a href="#card2Step3">STEP3：運用する銘柄や商品の選定</a></li>
      <li><a href="#card2Step4">STEP4：金融機関での口座開設</a></li>
      <li><a href="#card2Step5">TEP5：掛金設定と運用開始</a></li>
    </HeadlineListStepsSection>
    <h3 id="card2Step1">STEP1：加入資格の確認</h3>
    <p>
      iDeCoを利用できるのは、国民年金の被保険者資格に該当する方に限られ、加入区分ごとに拠出限度額も異なります。
    </p>
    <p>
      まずは以下の一覧表で、ご自身の加入資格とiDeCoの拠出限度額をご確認ください。
    </p>
    <ScrollTable>
      <caption>
        iDeCoの加入資格と拠出限度額
      </caption>
      <thead>
        <tr>
          <th>加入区分</th>
          <th>加入対象者</th>
          <th>非対象者</th>
          <th>拠出限度額</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>国民年金第1号被保険者</th>
          <td>20歳以上60歳未満の自営業者とその家族、フリーランス、学生など</td>
          <td>
            <ul>
              <li>農業者年金の被保険者</li>
              <li>国民年金保険料の納付免除者（一部免除を含む）※1</li>
            </ul>
          </td>
          <td>月額6.8万円<br />（年額81.6万円）</td>
        </tr>
        <tr>
          <th>国民年金第2号被保険者</th>
          <td>厚生年金の被保険者（会社員、公務員など）※2</td>
          <td>
            <ul>
              <li>
                勤務先で加入する「企業型確定拠出年金」の事業主掛金が、拠出限度額の範囲内での各月拠出となっていない方
              </li>
              <li>
                マッチング拠出（加入者も任意で掛金を拠出）を導入する企業型確定拠出年金の加入者、かつ企業型DCでマッチング拠出を選択した方
              </li>
            </ul>
          </td>
          <td>月額1.2万円〜2.3万円<br />（年額14.4万円〜27.6万円）</td>
        </tr>
        <tr>
          <th>国民年金第3号被保険者</th>
          <td>厚生年金の被保険者に扶養される20歳以上60歳未満の配偶者</td>
          <td>ー</td>
          <td>月額2.3万円<br />（年額27.6万円）</td>
        </tr>
        <tr>
          <th>国民年金任意加入被保険者</th>
          <td>
            国民年金の任意加入者
            <ul>
              <li>
                60歳以上65歳未満で国民年金保険料の納付済期間が480月に達していない方
              </li>
              <li>
                20歳以上65歳未満の海外居住者で国民年金保険料の納付済期間が480月に達していない方
              </li>
            </ul>
          </td>
          <td>ー</td>
          <td>月額6.8万円<br />（年額81.6万円）</td>
        </tr>
      </tbody>
    </ScrollTable>
    <p>
      ※1：障害基礎年金受給者は加入可能です<br />
      ※2：65歳以上の厚生年金被保険者、かつ加入期間が120月以上（10年以上）の方は対象外です<br />
      参考：<NuxtLink
        href="https://www.mhlw.go.jp/toukei/saikin/hw/life/life23/index.html"
        external
        target="_blank"
        >iDeCo（イデコ）の加入資格・掛金・受取方法等｜iDeCo公式サイト</NuxtLink
      >
    </p>
    <h3 id="card2Step2">STEP2：掛金の決定</h3>
    <p>
      iDeCoの最低拠出額は5,000円からとなっており、それ以上の金額については1,000円単位で自由に掛金を設定できます。
    </p>
    <p>
      掛金の設定後は、ご自身が指定した銀行口座から自動的に引き落とされるため、iDeCoの専用口座に入金する手間もかからず、効率良く資産運用に取り組める点が特徴です。
    </p>
    <p>
      ただし、iDeCoは原則として60歳になるまで一切の資産を引き出せないため、生活資金を除いた余剰資金の範囲内で掛金を設定することを心がけましょう。
    </p>
    <p>
      また、iDeCoの掛金はクレジットカードで支払うことができないのでご注意ください。
    </p>
    <h3 id="card2Step3">STEP3：運用する銘柄や商品の選定</h3>
    <p>掛金の設定後は、iDeCoで運用する銘柄や商品を選定しましょう。</p>
    <p>
      iDeCoで運用可能な銘柄は、大きく分けて「元本確保型」と「元本変動型」の2種類があります。
    </p>
    <ScrollTable>
      <caption>
        iDeCoで運用可能な銘柄の種類
      </caption>
      <tbody>
        <tr>
          <th>元本確保型</th>
          <td>
            購入に充てた金額（拠出額）を下回ることがない商品のこと<br />
            代表例としては「定期預金」、「保険（生命保険や損害保険）」などが挙げられる<br />
            元本が確保されている代わりに大きなリターンを得づらいことが特徴
          </td>
        </tr>
        <tr>
          <th>元本変動型</th>
          <td>
            購入に充てた金額（拠出額）が保証されていない商品のこと<br />
            代表例としては「投資信託」が挙げられる<br />
            価格変動による元本割れのリスクがある代わりに、値動きの大きい商品は高いリターンを期待できる
          </td>
        </tr>
      </tbody>
    </ScrollTable>
    <p>
      元本確保型の商品は、購入に充てた金額（拠出額）を下回ることがない商品のことで、代表例としては「定期預金」や「保険」などが挙げられます。
    </p>
    <p>
      大きなリターンを得づらい代わりに元本が確保されているため、iDeCoの税制上の優遇措置を活用しながら着実な資産形成に取り組みたい方におすすめです。
    </p>
    <p>
      ただし、iDeCo口座の開設や維持には手数料が必要です。元本確保型でも運用成績が振るわない場合は、いわゆる「手数料負け」が発生する可能性がある点には気をつけましょう。
    </p>
    <p>
      一方の元本変動型は、拠出額が保証されていない商品のことで、その代表例としては「投資信託」が挙げられます。
    </p>
    <p>
      価格変動による元本割れのリスクがある代わりに、値動きの大きい商品を選べばより高いリターンを期待できる点が特徴です。
    </p>
    <p>
      これから初めて資産運用に挑戦される方は、比較的低コストで運用可能な「インデックスファンド」を選ぶのが良いでしょう。
    </p>
    <h3 id="card2Step4">STEP4：金融機関での口座開設</h3>
    <p>
      運用する銘柄や商品の選定後は、その商品を取り扱っている金融機関で口座開設を申し込みます。
    </p>
    <p>
      申し込みの際には本人確認書類に加え、マイナンバー（個人番号）を確認できる書類の提出が必要です。
    </p>
    <p>
      iDeCo専用口座の開設には各金融機関共通で2,829円（税込）の手数料が発生します。
    </p>
    <p>
      それに加え、金融機関によって金額が異なる口座管理料も発生するので、iDeCo専用口座を開設する際は手数料が安い金融機関を選ぶことが大切です。
    </p>
    <p>
      なお、お申し込みから口座開設までにはおよそ2週間〜1カ月程度の時間がかかります。運用期間が長くなるほど有利な制度なので、なるべく早めに申し込み手続きを行いましょう。
    </p>
    <h3 id="card2Step5">STEP5：掛金設定と運用開始</h3>
    <p>
      iDeCo専用口座の開設後は、毎月の掛金を設定し、実際に運用をスタートするのみです。
    </p>
    <p>
      口座開設を申し込んだ金融機関によって異なりますが、基本的には取引画面にログインをすると、その時点におけるiDeCoの運用状況を確認できます。
    </p>
    <p>
      長期にわたって運用を続けることになるので、定期的にiDeCoの運用状況を確認し、必要に応じて運用銘柄や拠出額の見直しを行いましょう。
    </p>
    <p>
      なお、iDeCoへの掛金は後から変更することも可能ですが、掛金変更は毎年12月〜翌11月の期間中に1回までと決められています。
    </p>
    <p>
      たとえば、2024年8月に掛金変更を行った場合、2025年12月以降まで掛金変更ができないのでご注意ください。
    </p>
  </WhereToConsultCard>
</template>

<style lang="scss" module></style>
